import React, { useEffect, useState } from "react";
import {
    Stack,
    Text,
    ensure,
    FormValidationSummary,
    ValidationResult,
    validationFailed,
    createErrorFailure,
    ConfirmDialog
} from "sprint-common-web";

import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { ValidateMergeEpisodeVersionsRequest } from "../../../../api/ValidateMergeEpisodeVersionsRequest";
import { getMergeEpisodeVersionText } from "../EpisodeVersionListing";
import { EpisodeVersionListingItem } from "../EpisodeVersionListingItem";

interface MergeEpisodeVersionsConfirmationModalProps {
    tvSeriesId: string;
    episodeId: string;
    targetVersion?: EpisodeVersionListingItem;
    orphanedVersion?: EpisodeVersionListingItem;

    isOpen: boolean;
    isConfirmationDisabled: boolean;
    onConfirm: () => Promise<void>;
    onDismiss: () => void;
}

export const MergeEpisodeVersionsConfirmationModal = (
    props: MergeEpisodeVersionsConfirmationModalProps
): JSX.Element => {
    const { validateMergeEpisodeVersions } = useAssetsManageTvSeriesApi();

    const [isValidationPending, setIsValidationPending] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult>();

    useEffect(() => {
        setValidationResult(undefined);

        if (!props.isOpen) return;

        if (isAnyVersionMissing(props)) return;

        const { tvSeriesId, episodeId, targetVersion, orphanedVersion } = props;
        ensure(targetVersion);
        ensure(orphanedVersion);

        setIsValidationPending(true);

        validateMergeEpisodeVersions(tvSeriesId, createRequest(episodeId, targetVersion, orphanedVersion))
            .then((res) => {
                setValidationResult(res);
            })
            .catch((err) => {
                const failure = validationFailed([createErrorFailure(JSON.stringify(err))]);
                setValidationResult(failure);
            })
            .finally(() => setIsValidationPending(false));
    }, [props.isOpen]);

    const isAnyVersionMissing = ({ targetVersion, orphanedVersion }: MergeEpisodeVersionsConfirmationModalProps) =>
        !targetVersion || !orphanedVersion;

    if (isAnyVersionMissing(props)) return <></>;

    const isConfirmButtonDisabled = props.isConfirmationDisabled || isValidationPending || !validationResult?.isValid;

    return (
        <ConfirmDialog
            title="Merge versions"
            text="You are about to merge:"
            isBlocking={true}
            hidden={!props.isOpen}
            confirm={{ onClick: props.onConfirm, disabled: isConfirmButtonDisabled }}
            cancel={{ onClick: props.onDismiss }}
            width={800}
        >
            <Stack tokens={{ childrenGap: 10 }} styles={{ root: { minHeight: 130 } }}>
                <ConfirmationDetails target={props.targetVersion} orphan={props.orphanedVersion} />
                <FormValidationSummary validationResult={validationResult} isValidating={isValidationPending} />
            </Stack>
        </ConfirmDialog>
    );
};

interface ConfirmationDetailsProps {
    target?: EpisodeVersionListingItem;
    orphan?: EpisodeVersionListingItem;
}

const indentationStyles = { root: { textIndent: "20px" } };

const ConfirmationDetails = ({ target, orphan }: ConfirmationDetailsProps): JSX.Element => {
    if (!target || !orphan) return <></>;

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Text styles={indentationStyles}>
                <b>{getMergeEpisodeVersionText(orphan, false)}</b> (orphan)
            </Text>
            <Text>into</Text>
            <Text styles={indentationStyles}>
                <b>{getMergeEpisodeVersionText(target, false)}</b> (target).
            </Text>
        </Stack>
    );
};

const createRequest = (
    episodeId: string,
    targetVersion: EpisodeVersionListingItem,
    orphanedVersion: EpisodeVersionListingItem
): ValidateMergeEpisodeVersionsRequest => {
    return {
        episodeId,
        targetVersionId: targetVersion.id,
        orphanedVersionId: orphanedVersion.id
    };
};
